import {
  ActivityLogEvent,
  ActivityLogListQueryVariables,
  ModelType,
} from "@/gql/graphql";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Field } from "formik";
import { ActivityLogItem } from "@/app/ActivityLogs/Components/ActivityLogItem";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { ActivityIcon } from "lucide-react";
import { useId, useRef, useState } from "react";
import { FormikProps } from "formik/dist/types";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { cn } from "@/lib/utils";
import {
  getQueryKey,
  useGqlMutation,
  useInfiniteGqlQuery,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import { activityLogListQuery } from "@/app/ActivityLogs/GraphQL/activityLogListQuery";
import { createActivityLogMutation } from "@/app/ActivityLogs/GraphQL/activityLogMutations";
import { getActivityLogFeedQueryVariables } from "@/app/ActivityLogs/Utils/getActivityLogFeedQueryVariables";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

const schema = z.object({
  comment: z.string().min(1),
});

export type ActivityLogFeedProps = {
  modelId: string;
  modelType: ModelType;
  variables?: Partial<ActivityLogListQueryVariables>;
  onLogged?: () => void;
  className?: string;
  /** Hide the comment/log-a-call composer, leaving a read-only feed. */
  showComposer?: boolean;
};

export function ActivityLogFeed({
  modelId,
  modelType,
  variables,
  onLogged,
  className,
  showComposer = true,
}: ActivityLogFeedProps) {
  const t = useTranslations("activityLog.feed");
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const commentId = useId();
  const [submittingAction, setSubmittingAction] = useState<
    "comment" | "call" | null
  >(null);

  const queryVariables = getActivityLogFeedQueryVariables(
    modelId,
    modelType,
    variables,
  );

  const { mutateAsync } = useGqlMutation(createActivityLogMutation, {
    onSuccess: () => {
      onLogged?.();

      return queryClient.invalidateQueries({
        queryKey: getQueryKey(
          activityLogListQuery,
          queryVariables,
          orgId,
          true,
        ),
      });
    },
  });
  const ref = useRef<FormikProps<any>>(null);
  const containerRef = useRef<HTMLFormElement>(null);

  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteGqlQuery(activityLogListQuery, queryVariables, {
    initialPageParam: { page: 1 },
    getNextPageParam: (lastPage) => {
      return lastPage.activityLogs?.paginatorInfo.hasMorePages
        ? {
            page: lastPage.activityLogs.paginatorInfo.currentPage + 1,
          }
        : undefined;
    },
  });

  const logs =
    data?.pages.flatMap((page) => page.activityLogs?.data ?? []) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-12">
        <div className="mx-auto flex max-w-md flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <ActivityIcon className="h-6 w-6 text-red-400" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-gray-900">
            {t("errorLoading")}
          </h3>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
          >
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <ul role="list" className="flex flex-col-reverse gap-y-6">
        {logs.length === 0 ? (
          <div className="py-12">
            <div className="mx-auto flex max-w-md flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <ActivityIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="mt-4 text-sm font-medium text-gray-900">
                {t("noActivityYet")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">{t("getStarted")}</p>
            </div>
          </div>
        ) : null}
        {logs.map((activityItem, idx) => (
          <li key={activityItem.id} className="relative w-full text-xs">
            <div
              className={cn(
                idx === 0 ? "h-6" : "-bottom-6",
                "pointer-events-none absolute left-0 top-0 flex w-6 justify-center",
              )}
            >
              <div className="w-px bg-gray-200" />
            </div>
            <ActivityLogItem
              log={activityItem}
              modelType={modelType}
              modelId={modelId}
            />
          </li>
        ))}
      </ul>

      {hasNextPage ? (
        <div className="flex justify-center py-4">
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            {isFetchingNextPage ? (
              <Spinner className="h-4 w-4" />
            ) : (
              "Load older activity"
            )}
          </button>
        </div>
      ) : null}

      {showComposer ? (
        <div className="mt-6 flex">
          <GenericForm
            innerRef={ref as any}
            validationSchema={toFormikValidationSchema(schema)}
            initialValues={{
              comment: "",
              event: ActivityLogEvent.Note,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.metaKey) {
                setSubmittingAction("comment");
                ref.current?.handleSubmit();
              }

              if (e.key === "k" && e.metaKey) {
                setSubmittingAction("call");
                ref.current?.setFieldValue("event", ActivityLogEvent.PhoneCall);
                ref.current?.handleSubmit();
              }
            }}
            containerRef={containerRef}
            onSubmit={async (values, { resetForm }) => {
              try {
                await mutateAsync({
                  input: {
                    model_id: modelId,
                    model_type: modelType,
                    description: values.comment,
                    event: values.event,
                  },
                });

                resetForm();
                onLogged?.();
              } finally {
                setSubmittingAction(null);
              }
            }}
            className="relative block flex-auto"
          >
            <div className="shadow-xs overflow-hidden rounded-lg bg-white pb-12 ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600">
              <label htmlFor={commentId} className="sr-only">
                {t("addComment")}
              </label>
              <TextArea id={commentId} placeholder={t("placeholder")} />
            </div>

            <div className="absolute inset-x-0 bottom-0 flex justify-end space-x-2 py-2 pl-3 pr-2">
              <SubmitButton
                isLoading={submittingAction === "comment"}
                onClick={() => setSubmittingAction("comment")}
                label={t("comment")}
              />
              <LogCall
                isLoading={submittingAction === "call"}
                onClick={() => setSubmittingAction("call")}
                label={t("logCall")}
              />
            </div>
          </GenericForm>
        </div>
      ) : null}
    </div>
  );
}

function TextArea({ id, placeholder }: { id: string; placeholder: string }) {
  return (
    <Field
      as="textarea"
      rows={2}
      id={id}
      name="comment"
      className={cn(
        "outline-hidden focus:ring-0",
        "block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6",
        "px-2",
      )}
      placeholder={placeholder}
    />
  );
}

type SubmitButtonProps = {
  isLoading: boolean;
  onClick: () => void;
  label: string;
};

function SubmitButton({ isLoading, onClick, label }: SubmitButtonProps) {
  const { isSubmitting } = useFormState();

  return (
    <button
      disabled={isSubmitting}
      onClick={onClick}
      type="submit"
      className="shadow-xs relative flex items-center overflow-hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <Spinner className="h-4 w-4" />
        </div>
      ) : null}

      <span className={cn("flex items-center", isLoading && "opacity-0")}>
        <span className="mr-2 flex items-center space-x-1 text-xs">
          <span className="rounded bg-gray-100 px-1 py-0.5 font-mono">⌘</span>
          <span>+</span>
          <span className="rounded bg-gray-100 px-1 py-0.5 font-mono">↵</span>
        </span>
        <span>{label}</span>
      </span>
    </button>
  );
}

type LogCallProps = {
  isLoading: boolean;
  onClick: () => void;
  label: string;
};

function LogCall({ isLoading, onClick, label }: LogCallProps) {
  const { isSubmitting, submitForm, setFieldValue } = useFormState();

  const handleClick = () => {
    onClick();
    setFieldValue("event", ActivityLogEvent.PhoneCall);
    submitForm();
  };

  return (
    <button
      disabled={isSubmitting}
      onClick={handleClick}
      type="button"
      className="shadow-xs relative flex items-center overflow-hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <Spinner className="h-4 w-4" />
        </div>
      ) : null}

      <span className={cn("flex items-center", isLoading && "opacity-0")}>
        <span className="mr-2 flex items-center space-x-1 text-xs">
          <span className="rounded bg-gray-100 px-1 py-0.5 font-mono">⌘</span>
          <span>+</span>
          <span className="rounded bg-gray-100 px-1 py-0.5 font-mono">K</span>
        </span>
        <span>{label}</span>
      </span>
    </button>
  );
}
